import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { ResourceCategory, ResourceView } from "../../../hooks/useResources";
import { videoThumbnailFallback, videoThumbnailUrl } from "./VideoEmbed";
import styles from "./ResourceCard.module.css";

type Props = {
  resource: ResourceView;
};

const categoryLabel = (c: ResourceCategory): string => {
  switch (c) {
    case "tutorial":
      return "Tutorial";
    case "concept":
      return "Concept";
    case "troubleshooting":
      return "Troubleshooting";
    case "update":
      return "Update";
    case "inspiration":
      return "Inspiration";
  }
};

const categoryDotClass = (c: ResourceCategory): string => {
  switch (c) {
    case "tutorial":
      return styles.dotTutorial ?? "";
    case "concept":
      return styles.dotConcept ?? "";
    case "troubleshooting":
      return styles.dotTroubleshooting ?? "";
    case "update":
      return styles.dotUpdate ?? "";
    case "inspiration":
      return styles.dotInspiration ?? "";
  }
};

const categoryFallbackClass = (c: ResourceCategory): string => {
  switch (c) {
    case "tutorial":
      return styles.thumbFallbackTutorial ?? "";
    case "concept":
      return styles.thumbFallbackConcept ?? "";
    case "troubleshooting":
      return styles.thumbFallbackTroubleshooting ?? "";
    case "update":
      return styles.thumbFallbackUpdate ?? "";
    case "inspiration":
      return styles.thumbFallbackInspiration ?? "";
  }
};

const formatDuration = (seconds: number | null): string | null => {
  if (seconds === null || seconds <= 0) return null;
  const mm = Math.floor(seconds / 60);
  const ss = seconds % 60;
  return `${mm}:${ss.toString().padStart(2, "0")}`;
};

const PlayGlyph = (): JSX.Element => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden>
    <path d="M5 3.5v11l9-5.5-9-5.5z" fill="currentColor" />
  </svg>
);

export const ResourceCard = ({ resource }: Props): JSX.Element => {
  const [imgFailed, setImgFailed] = useState<boolean>(false);
  // YouTube's crisp 16:9 still (maxresdefault) only exists for uploads with a
  // 720p source, so a failed load steps down the chain rather than dropping
  // straight to the category panel.
  const [thumbStep, setThumbStep] = useState<number>(0);
  const navigate = useNavigate();

  const { videoProvider, videoId } = resource;
  const derived = videoProvider !== null && videoId !== null
    ? [
        videoThumbnailUrl(videoProvider, videoId),
        videoThumbnailFallback(videoProvider, videoId),
      ].filter((u): u is string => u !== null)
    : [];
  // A stored thumbnail wins: for Loom it is the hashed URL only oEmbed knows,
  // and for YouTube it is only ever set when an admin typed one by hand.
  const candidates = resource.thumbnailUrl !== null
    ? [resource.thumbnailUrl, ...derived]
    : derived;
  const thumbnail = candidates[thumbStep] ?? null;
  const duration = formatDuration(resource.durationSeconds);

  const onClick = (): void => {
    navigate(`/dashboard/resources/${resource.slug}`);
  };

  return (
    <button
      type="button"
      className={styles.card}
      aria-label={`Watch: ${resource.title}`}
      onClick={onClick}
    >
      <div className={styles.thumb}>
        {thumbnail && !imgFailed ? (
          <img
            src={thumbnail}
            alt=""
            className={styles.thumbImg}
            onError={() => {
              if (thumbStep + 1 < candidates.length) {
                setThumbStep(thumbStep + 1);
              } else {
                setImgFailed(true);
              }
            }}
          />
        ) : (
          <div className={`${styles.thumbFallback} ${categoryFallbackClass(resource.category)}`}>
            <div className={styles.thumbGrain} aria-hidden />
          </div>
        )}
        <div className={styles.playButton} aria-hidden>
          <PlayGlyph />
        </div>
        {duration && <span className={styles.duration}>{duration}</span>}
      </div>
      <div className={styles.body}>
        <div className={styles.metaRow}>
          <span className={`${styles.dot} ${categoryDotClass(resource.category)}`} />
          <span className={styles.category}>{categoryLabel(resource.category)}</span>
        </div>
        <h3 className={styles.title}>{resource.title}</h3>
      </div>
    </button>
  );
};
